import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import { useDebouncedValue, useRequestGate } from '../../hooks/useLatestRequest';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../utils/LocaleContext';
import { useTenantProducts, productLabel } from '../../utils/productCatalog';

// const MOCK_POLICIES = [
//   { id: 'POL-RC-2026-0042',  client: 'Dr. Martin Weber',       company: 'Praxis Weber GmbH',          product: 'Professional Liability', status: 'active',    premium: 8400,   start: '2026-01-01', end: '2026-12-31', broker: 'Fiduciaire Romande SA', claims: 1, canton: 'ZH' },
//   { id: 'POL-IJM-2026-0189', client: 'Clinique du Lac SA',     company: 'Clinique du Lac SA',         product: 'Daily Sickness',         status: 'active',    premium: 22000,  start: '2026-01-01', end: '2026-12-31', broker: 'Assura Broker AG',       claims: 1, canton: 'VD' },
//   { id: 'POL-PJ-2026-0331',  client: 'Dr. Sophie Müller',     company: 'Kinderarztpraxis Müller',    product: 'Legal Protection',       status: 'active',    premium: 2800,   start: '2026-01-01', end: '2026-12-31', broker: 'Helvetica Partners',     claims: 1, canton: 'BE' },
//   { id: 'POL-RC-2026-0015',  client: 'Dr. Jean-Luc Favre',    company: 'Cabinet Favre',              product: 'Professional Liability', status: 'active',    premium: 12500,  start: '2026-01-01', end: '2026-12-31', broker: 'Fiduciaire Romande SA',  claims: 1, canton: 'GE' },
//   { id: 'POL-IJM-2026-0092', client: 'Gruppenpraxis Bern AG', company: 'Gruppenpraxis Bern AG',      product: 'Daily Sickness',         status: 'active',    premium: 45000,  start: '2026-01-01', end: '2026-12-31', broker: 'Helvetica Partners',     claims: 1, canton: 'BE' },
//   { id: 'POL-RC-2026-0008',  client: 'Dr. Elena Rossi',       company: 'Studio Medico Rossi',        product: 'Professional Liability', status: 'active',    premium: 6800,   start: '2026-01-01', end: '2026-12-31', broker: 'Ticino Broker SA',        claims: 1, canton: 'TI' },
//   { id: 'POL-PJ-2025-0287',  client: 'Centre Médical Lausanne',company: 'Centre Médical Lausanne SA',product: 'Legal Protection',       status: 'expired',   premium: 5200,   start: '2025-01-01', end: '2025-12-31', broker: 'Assura Broker AG',       claims: 1, canton: 'VD' },
//   { id: 'POL-IJM-2026-0045', client: 'Dr. Thomas Brunner',    company: 'Praxis Brunner',             product: 'Daily Sickness',         status: 'active',    premium: 15000,  start: '2026-01-01', end: '2026-12-31', broker: 'Helvetica Partners',     claims: 1, canton: 'AG' },
//   { id: 'POL-RC-2025-0091',  client: 'Dr. Anna Meier',        company: 'Praxis Meier',               product: 'Professional Liability', status: 'cancelled', premium: 7200,   start: '2025-03-01', end: '2025-08-31', broker: 'Assura Broker AG',       claims: 0, canton: 'ZH' },
//   { id: 'POL-IJM-2026-0201', client: 'Praxis am See AG',      company: 'Praxis am See AG',           product: 'Daily Sickness',         status: 'active',    premium: 31000,  start: '2026-02-01', end: '2027-01-31', broker: 'Fiduciaire Romande SA',  claims: 0, canton: 'ZH' },
// ];

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

// Decision-shaped status filter. Policy.status has no 'approved' or
// 'rejected' member — these are groupings the backend expands over the
// statuses that do exist (approved → active|bound, rejected →
// cancelled), so there is no schema change and the pagination total
// stays honest. Platform behaviour: every tenant gets this filter.
const STATUS_OPTIONS = ['all', 'approved', 'rejected'];
const ALL_PRODUCTS = 'All Products';

const usePolicyFunctions = () => {
  useLocale();
  const { t } = useTranslation();
  const history = useHistory();
  const products = useTenantProducts();
  const [policies, setPolicies] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  // Query on the settled value, and let only the newest request's reply
  // reach the screen -- see hooks/useLatestRequest.js.
  const debouncedSearch = useDebouncedValue(search, 300);
  const beginRequest = useRequestGate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [productFilter, setProductFilter] = useState(ALL_PRODUCTS);
  // The product values this tenant's policies actually carry, from
  // GET /policies/facets. Null until it resolves.
  const [facetProducts, setFacetProducts] = useState(null);
  const [page, setPage] = useState(1);
  const [showNew, setShowNew] = useState(false);
  // X-02: page size is now user-controllable via PageSizeSelector.
  // Default stays at 20 to preserve historical behaviour; reset to
  // page 1 whenever the size changes so we never sit on a page number
  // that no longer exists.
  const [PAGE_SIZE, setPageSize] = useState(20);

  useEffect(() => {
    let ignore = false;
    axios.get(`${BASE_URL}/policies/facets`, { withCredentials: true })
      .then(r => { if (!ignore) setFacetProducts(Array.isArray(r?.data?.data?.products) ? r.data.data.products : []); })
      // On failure fall through to the allow-list below rather than showing
      // no filter at all — the register stays usable.
      .catch(() => { if (!ignore) setFacetProducts(null); });
    return () => { ignore = true; };
  }, []);

  // What the product filter offers.
  //
  // A FILTER offers what is in the data; a PICKER offers what the tenant may
  // choose. They were the same list, drawn from the tenant's product
  // allow-list — and resolveProducts() falls back to the whole platform
  // catalogue when a tenant has configured none (see
  // [[gotcha-empty-config-means-platform-default]], a fallback that is
  // deliberate and must not change). MOK configures no products, so its
  // register offered five product families it does not sell and none of which
  // could ever match a row.
  //
  // Facets first, then the allow-list as a fallback for the window before they
  // resolve and for a tenant with no policies yet. Values stay LOB CODES so
  // the backend ?lob query matches — the label is translated at render time.
  const productChoices = facetProducts && facetProducts.length ? facetProducts : products;
  const PRODUCT_OPTIONS = [ALL_PRODUCTS, ...productChoices];

  const toDisplayPolicy = useCallback(
    p => {
      // Same broker resolution as the offers list — accept populated
      // {_id, name} shape, stored brokerName, or fall back to '—'.
      // Never surface the raw ObjectId.
      const brokerName = p.brokerName
        || (p.broker && typeof p.broker === 'object' ? p.broker.name : null);
      return {
        id: p.policyNumber || p._id,
        client: p.policyholder?.name || p.client || '—',
        company: p.policyholder?.company || p.company || '—',
        // Product code wins over the family enum: three products
        // (PGMA/PGM/IJM) share the daily_sickness family, so the family
        // alone renders a PGMA policy under the IJM name. Both routed
        // through i18n — this column used to be hardcoded English and
        // stayed English with the interface in French.
        product: productLabel(t, p.product, p.lineOfBusiness, 'short'),
        status: p.status || 'active',
        premium: p.annualPremium || p.premium || 0,
        // The record's own currency, so the register stops relabelling an
        // EUR policy in the reader's preferred currency.
        currency: p.currency || null,
        start: p.startDate || p.start,
        end: p.endDate || p.end,
        broker: brokerName || '—',
        // null, not 0. `claimCount` is not in the policy list projection, so
        // `|| 0` printed a confident zero for policies that do have linked
        // cases. Absent is not none; the column renders null as an em dash.
        claims: p.claimCount ?? p.claims ?? null,
        canton: p.canton || '',
        _id: p._id,
      };
    },
    [t],
  );

  const fetchPolicies = useCallback(async () => {
    const req = beginRequest();
    try {
      const params = new URLSearchParams({ page, limit: PAGE_SIZE });
      if (statusFilter !== 'all') params.set('status', statusFilter);
      // D-20 retest: send the product CODE (RC_PRO/PGMA/PGM) directly.
      // Backend now matches EITHER product OR lineOfBusiness so PGMA/PGM
      // don't collapse into the shared daily_sickness family.
      if (productFilter !== ALL_PRODUCTS) params.set('lob', productFilter);
      if (debouncedSearch) params.set('search', debouncedSearch);
      const resp = await axios.get(`${BASE_URL}/policies?${params}`, { withCredentials: true , signal: req.signal });
      if (!req.isCurrent()) return;
      if (resp.data?.data) {
        setPolicies(resp.data.data.map(toDisplayPolicy));
        setTotal(resp.data.total || resp.data.data.length);
      }
    } catch {
      /* empty state on error */
    } finally {
      if (req.isCurrent()) setLoading(false);
    }
  }, [page, statusFilter, productFilter, debouncedSearch, toDisplayPolicy, PAGE_SIZE, beginRequest]);

  useEffect(() => {
    fetchPolicies();
  }, [fetchPolicies]);

  const handleCloseNew = useCallback(() => setShowNew(false), []);
  const handleCreated = useCallback(() => {
    fetchPolicies();
    setShowNew(false);
  }, [fetchPolicies]);

  const filtered = policies.filter(p => {
    // D-20 retest: backend already narrows on status + lob; do NOT
    // re-filter on those here. The previous code compared productFilter
    // (a LOB CODE like RC_PRO) against p.product (a translated label
    // like "Professional Liability (RC Pro)") and zeroed out every row.
    const q = search.toLowerCase();
    if (q && !p.id.toLowerCase().includes(q) && !p.client.toLowerCase().includes(q) && !(p.broker || '').toLowerCase().includes(q)) return false;
    return true;
  });

  const paginated = filtered;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return {
    STATUS_OPTIONS,
    PRODUCT_OPTIONS,
    history,
    policies,
    loading,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    productFilter,
    setProductFilter,
    page,
    setPage,
    pageSize: PAGE_SIZE,
    setPageSize: (n) => { setPageSize(n); setPage(1); },
    showNew,
    setShowNew,
    handleCloseNew,
    handleCreated,
    filtered,
    paginated,
    totalPages,
  };
};

export default usePolicyFunctions;
